import React, { useState, useEffect } from 'react';
import ProjectForm from './ProjectForm';
import useIsMobile from '../../../../../hooks/useIsMobile';

// Modal for naming a new project (or renaming an existing one before Save Changes).
//
// Props:
//   open       - boolean
//   mode       - 'create' | 'edit'   (changes header + button label)
//   initial    - { name, description } prefilled values (used in edit mode)
//   loading    - external save-in-progress flag (disables form + button)
//   onClose()  - dismiss without saving
//   onSubmit({ name, description }) - parent persists; should throw to surface errors
export default function SaveProjectModal({
  open, mode = 'create', initial = {}, loading = false, onClose, onSubmit,
}) {
  const isMobile = useIsMobile();
  const [values, setValues] = useState({ name: '', description: '', clientDetails: {} });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (open) {
      setValues({ name: initial.name || '', description: initial.description || '', clientDetails: initial.clientDetails || {} });
      setErrors({});
      setSubmitError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial.name, initial.description]);

  if (!open) return null;

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = 'Project name is required';
    else if (values.name.trim().length < 2) next.name = 'Name must be at least 2 characters';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError(null);
    try {
      await onSubmit({ name: values.name.trim(), description: values.description.trim(), clientDetails: values.clientDetails || {} });
    } catch (err) {
      setSubmitError(err?.message || 'Failed to save project');
    }
  };

  return (
    <div style={isMobile ? backdropStyleMobile : backdropStyle} onClick={loading ? undefined : onClose}>
      <div style={isMobile ? containerStyleMobile : containerStyle} onClick={(e) => e.stopPropagation()}>
        {/* Drag handle — mobile only, matches every other bottom sheet in
            the editor (MobileSheetWrapper) — this modal isn't rendered
            inside that shared component (it's opened from a ref-exposed
            save() call, not the mobileSheet state that drives the others),
            so it gets its own matching sheet chrome directly instead. */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 2px', flexShrink: 0 }}>
            <div style={{ width: 38, height: 4, borderRadius: 999, background: '#e2e8f0' }} />
          </div>
        )}
        <div style={isMobile ? headerStyleMobile : headerStyle}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#111827' }}>
            {mode === 'edit' ? 'Save Changes' : 'Create New Project'}
          </h3>
          <button type="button" onClick={onClose} disabled={loading} style={closeBtnStyle}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={isMobile ? { display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 } : undefined}>
          <div style={isMobile ? bodyStyleMobile : bodyStyle}>
            <ProjectForm
              values={values}
              onChange={setValues}
              errors={errors}
              disabled={loading}
              showClientFields={mode === 'create'}
            />
            {submitError && (
              <div style={errorBoxStyle}>{submitError}</div>
            )}
          </div>

          <div style={isMobile ? footerStyleMobile : footerStyle}>
            <button type="button" onClick={onClose} disabled={loading} style={btnSecondary}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={btnPrimary}>
              {loading
                ? (mode === 'edit' ? 'Saving...' : 'Creating...')
                : (mode === 'edit' ? 'Save Changes' : 'Create Project')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const backdropStyle = {
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const containerStyle = {
  background: '#fff', borderRadius: 10, width: '92%', maxWidth: 480,
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

// Mobile: same slide-up-sheet look as MobileSheetWrapper (bottom-anchored,
// rounded top corners only, capped height with internal scroll) — this
// modal has its own root instead of being rendered through that shared
// component (see drag-handle comment above), so its backdrop/container are
// hand-matched to it rather than sharing code.
const backdropStyleMobile = {
  // Above MobileBottomToolbar's z-index (10100) — otherwise the floating
  // bottom nav bar rendered on top of this sheet instead of sitting behind
  // it, per reported bug (screenshot showed the toolbar icons overlapping
  // the sheet's bottom edge).
  position: 'fixed', inset: 0, zIndex: 10300,
  background: 'rgba(15,23,42,0.5)',
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
};
const containerStyleMobile = {
  background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '100%',
  maxHeight: '85vh', boxShadow: '0 -8px 30px rgba(15,23,42,0.25)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
};
const headerStyle = {
  padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const headerStyleMobile = { ...headerStyle, padding: '4px 18px 14px', flexShrink: 0 };
const closeBtnStyle = {
  background: 'transparent', border: 'none', fontSize: 22, lineHeight: 1,
  color: '#6b7280', cursor: 'pointer', padding: 0, width: 28, height: 28,
};
const bodyStyle = { padding: '18px' };
const bodyStyleMobile = { ...bodyStyle, overflowY: 'auto', minHeight: 0 };
const footerStyle = {
  padding: '12px 18px', borderTop: '1px solid #e5e7eb',
  display: 'flex', justifyContent: 'flex-end', gap: 8,
};
const footerStyleMobile = { ...footerStyle, flexShrink: 0 };
const btnPrimary = {
  background: '#00A19D', color: '#fff', border: 'none', borderRadius: 6,
  padding: '8px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
};
const btnSecondary = {
  background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6,
  padding: '8px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
};
const errorBoxStyle = {
  marginTop: 12, padding: '8px 10px', background: '#FEF2F2',
  color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 6, fontSize: 13,
};
